import type { SecuredFlag } from '@components/security/types';
import type { FieldKey, ViewColumn } from '../views/types';

/**
 * How a table's fields become columns. A table declares a catalogue of fields (one value each) and
 * presets (named stacks of fields, e.g. today's "Route" cell); a view arranges them into columns.
 * Everything a column needs — heading, width, sort, pinning — is derived from its members here, so a
 * user only ever places fields and never has to name or size a column.
 */

export interface FieldMeta {
  key: FieldKey;
  /** Translation key + fallback of the field's own name. */
  header: string;
  headerDefault: string;
  /** Section of the column sidebar this field is listed under. */
  group: string;
  /** The column's default width: a narrow pane scrolls instead of squeezing the column. */
  minWidth?: number;
  /**
   * The narrowest a user may drag a column holding this field: what its component (a state badge,
   * a price tag, the tender progress) needs to stay whole. Text fields leave it out — they end in an
   * ellipsis with the full text on hover, so they can go down to MIN_RESIZE_WIDTH.
   */
  fitWidth?: number;
  /** Field the API sorts by, where there is one. */
  sortField?: string;
  /**
   * Flows in a wrapping row with the neighbouring inline members of a stack instead of taking a line
   * of its own — tags, which today's cells lay out side by side.
   */
  inline?: boolean;
  /** `false`: a control, not data (e.g. "expand all"), left out of the export. */
  exportable?: boolean;
  /** Can't be switched off: the table needs it (the identity field, which links to the detail). */
  required?: boolean;
  /** The column holding this field is pinned left while it is the first column. */
  identity?: boolean;
  /**
   * Permission gate (e.g. the price flag). Not granted: the field is neither listed nor rendered, but
   * stays in stored tabs — see fieldAccess.ts.
   */
  secured?: SecuredFlag;
}

export interface FieldPreset {
  key: string;
  header: string;
  headerDefault: string;
  group: string;
  members: FieldKey[];
  /**
   * Width of a column that is exactly this preset. A stack needs more room than its widest field —
   * a badge next to a link, two lines of a route — so presets are sized like the cells they replace.
   */
  width?: number;
}

export type Translate = (key: string, fallback: string) => string;

const DEFAULT_MIN_WIDTH = 120;

const sameStack = (a: FieldKey[], b: FieldKey[]) => a.length === b.length && a.every((key, i) => key === b[i]);

/**
 * The preset a column is exactly — Ringil's own column, untouched. Moving, adding or removing any of
 * its fields makes it the user's own stack, and its preset switch goes off.
 */
export const matchingPreset = (column: ViewColumn, presets: FieldPreset[]): FieldPreset | undefined => presets.find(p => sameStack(p.members, column.members));

/**
 * A column that is exactly a preset is titled like the preset ("Route"), as the cell it replaces was.
 * Any other stack is titled by its members, "Carrier / Total price" as in the Figma — not a name the
 * user types, which would go stale the moment a field moves.
 */
export const columnHeading = (column: ViewColumn, fields: Map<FieldKey, FieldMeta>, presets: FieldPreset[], tr: Translate): string => {
  // A heading the user gave the column wins, shown as typed. (The column sidebar shows the field's own
  // name under it, so the user still sees what a renamed column holds.)
  if (column.title) return column.title;
  return derivedColumnHeading(column, fields, presets, tr);
};

/** The heading a column gets from its members alone — the placeholder while renaming it. */
export const derivedColumnHeading = (column: ViewColumn, fields: Map<FieldKey, FieldMeta>, presets: FieldPreset[], tr: Translate): string => {
  const preset = matchingPreset(column, presets);
  if (preset) return tr(preset.header, preset.headerDefault);
  return column.members
    .map(key => fields.get(key))
    .filter(field => field?.header || field?.headerDefault)
    .map(field => tr(field.header, field.headerDefault))
    .join(' / ');
};

/** A stack is only as narrow as its widest member allows. */
export const columnMinWidth = (column: ViewColumn, fields: Map<FieldKey, FieldMeta>): number =>
  column.members.reduce((widest, key) => Math.max(widest, fields.get(key)?.minWidth ?? 0), 0) || DEFAULT_MIN_WIDTH;

/** The narrowest any column may be dragged to — the same as the narrowest stored width (views/columnOps). */
export const MIN_RESIZE_WIDTH = 60;

/** How narrow the user may make a column: as narrow as its least squeezable member allows. */
export const columnFloorWidth = (column: ViewColumn, fields: Map<FieldKey, FieldMeta>): number =>
  column.members.reduce((widest, key) => Math.max(widest, fields.get(key)?.fitWidth ?? 0), MIN_RESIZE_WIDTH);

/**
 * The width the column is drawn at: the user's own when they sized it — but never below its floor, so
 * a component added later to a column dragged narrow still fits — otherwise the derived one.
 */
export const effectiveColumnWidth = (column: ViewColumn, fields: Map<FieldKey, FieldMeta>, presets: FieldPreset[]): number =>
  column.width ? Math.max(column.width, columnFloorWidth(column, fields)) : columnWidth(column, fields, presets);

/**
 * The column's width: a preset's own width when the column is exactly that preset, otherwise its
 * widest member's — never narrower than that member needs.
 */
export const columnWidth = (column: ViewColumn, fields: Map<FieldKey, FieldMeta>, presets: FieldPreset[]): number => {
  const preset = matchingPreset(column, presets);
  return Math.max(preset?.width ?? 0, columnMinWidth(column, fields));
};

/**
 * A stack sorts by its first sortable member. Refusing to sort mixed stacks would make grouping a
 * sortable field with any other one silently cost the user their sort.
 */
export const columnSortField = (column: ViewColumn, fields: Map<FieldKey, FieldMeta>): string | undefined =>
  column.members.map(key => fields.get(key)?.sortField).find(Boolean);

/**
 * Pins the identity column left, but only while it is the first column: pinned further right it
 * would slide over the columns before it instead of staying put.
 */
export const isIdentityColumnPinned = (column: ViewColumn, index: number, fields: Map<FieldKey, FieldMeta>): boolean =>
  index === 0 && column.members.some(key => fields.get(key)?.identity);

export type StackRun = { kind: 'block'; key: FieldKey } | { kind: 'inline'; keys: FieldKey[] };

/**
 * Splits a stack into what the cell draws: each block member on its own line, and consecutive inline
 * members (tags) gathered into one wrapping row — so stacking the "additional info" tags reads like
 * today's tag cell rather than one tag per line.
 */
export const stackRuns = (members: FieldKey[], fields: Map<FieldKey, FieldMeta>): StackRun[] =>
  members.reduce<StackRun[]>((runs, key) => {
    if (!fields.get(key)?.inline) return [...runs, { kind: 'block', key }];
    const last = runs[runs.length - 1];
    if (last?.kind === 'inline') return [...runs.slice(0, -1), { kind: 'inline', keys: [...last.keys, key] }];
    return [...runs, { kind: 'inline', keys: [key] }];
  }, []);

/** Fields the sidebar offers but that sit in no column. */
export const availableFields = (fields: FieldMeta[], columns: ViewColumn[]): FieldMeta[] => {
  const placed = new Set(columns.flatMap(column => column.members));
  return fields.filter(field => !placed.has(field.key));
};

/** A view must keep its required fields; columns missing one get it back as a leading column. */
export const withRequiredFields = (columns: ViewColumn[], fields: FieldMeta[], newId: () => string): ViewColumn[] => {
  const placed = new Set(columns.flatMap(column => column.members));
  const missing = fields.filter(field => field.required && !placed.has(field.key)).map(field => field.key);
  return missing.length > 0 ? [{ id: newId(), members: missing }, ...columns] : columns;
};
